import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { colors, fonts, spacing } from '../theme';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import SearchBar from '../components/SearchBar';
import { supabase } from '../lib/supabase';
import { fetchSuggestedClients } from './hooks/useIncomingRecords';
import { useMatching } from './hooks/useMatching';

function IncomingDetailPanel({ record, onClose, onUpdated }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [dormantEmail, setDormantEmail] = useState(record.email_hint || '');
  const [dormantName, setDormantName] = useState(record.name_hint || '');
  const [busy, setBusy] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const { matchRecord, unmatchRecord, ignoreRecord, createDormantAccount, runWithToast } =
    useMatching();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!record.email_hint) {
        setSuggestions([]);
        return;
      }
      setLoadingSuggestions(true);
      const data = await fetchSuggestedClients(record.email_hint);
      if (!cancelled) {
        setSuggestions(data);
        setLoadingSuggestions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [record.email_hint]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientSearch.trim() || !supabase) {
        setClientResults([]);
        return;
      }
      const term = clientSearch.trim();
      const { data } = await supabase
        .from('clients')
        .select('id, login_email, owner_email, business_name, owner_name, account_state')
        .or(
          `login_email.ilike.%${term}%,owner_email.ilike.%${term}%,business_email.ilike.%${term}%,business_name.ilike.%${term}%,owner_name.ilike.%${term}%`
        )
        .is('deleted_at', null)
        .limit(15);
      if (!cancelled) setClientResults(data || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [clientSearch]);

  const handleMatch = async (clientId, force = false) => {
    setBusy(true);
    try {
      await runWithToast('Record matched', () => matchRecord(record.id, clientId, force));
      setMatchModalOpen(false);
      onUpdated?.();
    } catch (err) {
      if (err.code === 'identity_conflict' && window.confirm(`${err.message}\n\nForce match anyway?`)) {
        await handleMatch(clientId, true);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUnmatch = async () => {
    if (!window.confirm('Remove match from this record?')) return;
    setBusy(true);
    try {
      await runWithToast('Match removed', () => unmatchRecord(record.id));
      onUpdated?.();
    } finally {
      setBusy(false);
    }
  };

  const handleIgnore = async () => {
    if (!window.confirm('Mark this item as ignored?')) return;
    setBusy(true);
    try {
      await runWithToast('Item ignored', () => ignoreRecord(record.id));
      onUpdated?.();
    } finally {
      setBusy(false);
    }
  };

  const handleCreateDormant = async () => {
    if (!dormantEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    setBusy(true);
    try {
      await runWithToast('Dormant account created', () =>
        createDormantAccount({
          email: dormantEmail.trim(),
          name: dormantName.trim() || null,
          incomingRecordId: record.id,
        })
      );
      onUpdated?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Matching workbench"
      header={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <h2 style={{ fontFamily: fonts.heading, fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
            Matching workbench
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: '0.9rem', marginBottom: spacing.md }}>
        <div style={{ marginBottom: spacing.xs }}>
          <strong>Source:</strong> {record.source} · <strong>Type:</strong> {record.type}
        </div>
        <div style={{ marginBottom: spacing.xs }}>
          <strong>External ID:</strong> {record.external_id}
        </div>
        <div style={{ marginBottom: spacing.xs }}>
          <strong>Email:</strong> {record.email_hint || '—'} · <strong>Name:</strong>{' '}
          {record.name_hint || '—'}
        </div>
        <div style={{ marginBottom: spacing.sm }}>
          <strong>Status:</strong> {record.status}
          {record.matched_client_id && (
            <span> · Client #{record.matched_client_id}</span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowPayload((v) => !v)}>
          {showPayload ? 'Hide' : 'Show'} raw payload
        </Button>
        {showPayload && (
          <pre
            style={{
              marginTop: spacing.sm,
              maxHeight: 200,
              overflow: 'auto',
              fontSize: '0.7rem',
              backgroundColor: colors.gray100,
              padding: spacing.sm,
              borderRadius: 8,
            }}
          >
            {JSON.stringify(record.payload, null, 2)}
          </pre>
        )}
      </div>

      {record.status === 'suggested' && suggestions.length > 0 && (
        <div style={{ marginBottom: spacing.md }}>
          <h4 style={{ fontFamily: fonts.heading, fontSize: '0.95rem', marginBottom: spacing.sm }}>
            Suggested matches (exact email)
          </h4>
          {loadingSuggestions ? (
            <p style={{ color: colors.midGray }}>Loading…</p>
          ) : (
            suggestions.map((client) => (
              <div
                key={client.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: spacing.sm,
                  border: `1px solid ${colors.gray200}`,
                  borderRadius: 8,
                  marginBottom: spacing.xs,
                  backgroundColor: '#fffbeb',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {client.business_name || client.owner_name || `Client #${client.id}`}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: colors.midGray }}>
                    {client.login_email || client.owner_email} · {client.account_state || 'active'}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  loading={busy}
                  onClick={() => handleMatch(client.id)}
                >
                  Match
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.md }}>
        {record.status !== 'matched' && (
          <>
            <Button variant="primary" size="sm" onClick={() => setMatchModalOpen(true)}>
              Match to…
            </Button>
            <Button variant="outline" size="sm" loading={busy} onClick={handleCreateDormant}>
              Create dormant & attach
            </Button>
          </>
        )}
        {record.status === 'matched' && (
          <Button variant="outline" size="sm" loading={busy} onClick={handleUnmatch}>
            Unmatch
          </Button>
        )}
        {record.status !== 'ignored' && (
          <Button variant="ghost" size="sm" loading={busy} onClick={handleIgnore}>
            Ignore
          </Button>
        )}
      </div>

      {record.status !== 'matched' && (
        <div style={{ borderTop: `1px solid ${colors.gray200}`, paddingTop: spacing.md }}>
          <h4 style={{ fontFamily: fonts.heading, fontSize: '0.95rem', marginBottom: spacing.sm }}>
            Create dormant account
          </h4>
          <Input label="Email" value={dormantEmail} onChange={(e) => setDormantEmail(e.target.value)} />
          <div style={{ marginTop: spacing.sm }}>
            <Input label="Name (optional)" value={dormantName} onChange={(e) => setDormantName(e.target.value)} />
          </div>
        </div>
      )}

      <Modal isOpen={matchModalOpen} onClose={() => setMatchModalOpen(false)} title="Match to account">
        <SearchBar value={clientSearch} onChange={setClientSearch} placeholder="Search accounts…" />
        <div style={{ marginTop: spacing.md, maxHeight: 320, overflow: 'auto' }}>
          {clientResults.map((client) => (
            <div
              key={client.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: spacing.sm,
                borderBottom: `1px solid ${colors.gray200}`,
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>
                  {client.business_name || client.owner_name || `Client #${client.id}`}
                </div>
                <div style={{ fontSize: '0.85rem', color: colors.midGray }}>
                  {client.login_email || client.owner_email} · {client.account_state || 'active'}
                </div>
              </div>
              <Button variant="primary" size="sm" loading={busy} onClick={() => handleMatch(client.id)}>
                Select
              </Button>
            </div>
          ))}
          {clientSearch && clientResults.length === 0 && (
            <p style={{ color: colors.midGray, padding: spacing.sm }}>No accounts found.</p>
          )}
        </div>
      </Modal>
    </Card>
  );
}

export default IncomingDetailPanel;
